import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Factory, Warehouse, Fingerprint, Wrench, BarChart3, Network,
  Smartphone, Bell, MessageSquare, PenTool, QrCode, ScanLine,
  Wifi, MapPin, CheckCircle2, Sparkles, Cpu, Signal, PhoneCall,
  ClipboardCheck, ArrowRight, Terminal, ShieldCheck, Server, Lock, HardDrive,
} from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { spotlightTilt } from "@/components/site/fx";
import { lenisRef } from "@/lib/lenis";
import { useI18n } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

const C = {
  ro: {
    label: "Software la comandă",
    h1: "De la ZERO.",
    h2: "100% pe fluxul tău.",
    sub: "Nu-ți schimbăm noi modul de lucru — ne mulăm noi pe al tău. Ne spui A, B, C… Z, iar noi îți transformăm cererea în aplicație reală: producție, utilaje, depozit, mentenanță — orice flux. Totul controlat din telefon: notificări, SMS, aprobări. Oriunde ești.",
    colClient: "Clientul cere",
    colTransform: "Noi transformăm",
    colPhone: "Realitate, în buzunar",
    requests: [
      "vreau să văd stocul în timp real",
      "alertă SMS când un utilaj se oprește",
      "aprobări direct din telefon",
      "raport automat la sfârșitul turei",
      "scanare QR pe fiecare comandă",
      "semnătură digitală pe teren",
    ],
    steps: ["Analiză flux", "Design & arhitectură", "Dezvoltare custom", "Live + suport"],
    appName: "DMO · Live Ops",
    live: "LIVE",
    notifs: [
      { type: "alert", title: "Utilaj #3 — OPRIT", sub: "Linia B · acum 12s" },
      { type: "sms", title: "SMS trimis", sub: "Comandă #4821 aprobată" },
      { type: "ok", title: "Stoc actualizat", sub: "+120 buc · Depozit A" },
      { type: "task", title: "Mentenanță programată", sub: "Mâine 08:00 · Presa 2" },
      { type: "sign", title: "Semnătură primită", sub: "Bon livrare · Client X" },
      { type: "call", title: "Apel automat dispecer", sub: "Prioritate ridicată" },
    ],
    mobileTitle: "Totul din telefon",
    mobileSub: "Orice acțiune, oriunde — fără să fii legat de birou.",
    chips: [
      { icon: Bell, t: "Notificări push" },
      { icon: MessageSquare, t: "SMS & alerte" },
      { icon: CheckCircle2, t: "Aprobări 1-tap" },
      { icon: QrCode, t: "Scanare QR / coduri" },
      { icon: PenTool, t: "Semnătură digitală" },
      { icon: BarChart3, t: "Rapoarte live" },
      { icon: Wifi, t: "Mod offline" },
      { icon: MapPin, t: "GPS & locație" },
    ],
    examplesTitle: "Exemple de fluxuri pe care le-am putea transforma",
    examplesSub: "Câteva scenarii reale. Al tău e diferit? Perfect — îl construim exact cum lucrezi tu.",
    examples: [
      {
        icon: Factory, title: "Producție & utilaje",
        desc: "Monitorizezi utilajele în timp real: opriri, OEE, consum. Când o mașină se oprește, primești SMS instant și un tichet de mentenanță se deschide automat.",
        flow: ["Senzori / PLC", "Dashboard live", "SMS + tichet"],
        tags: ["OEE", "Downtime", "Mentenanță", "Alerte"],
      },
      {
        icon: Warehouse, title: "Depozit & logistică",
        desc: "Stoc live pe telefon, scanare QR la recepție și expediere, avize generate automat. Zero hârtii, zero erori de numărare.",
        flow: ["Scanare QR", "Stoc real-time", "Aviz automat"],
        tags: ["Scanare", "Stoc", "Comenzi", "Trasabilitate"],
      },
      {
        icon: Fingerprint, title: "Control acces & pontaj",
        desc: "Pontaj automat, acces pe bază de rol și rapoarte de prezență. Managerul vede live cine e pe tură și primește alertă la evenimente.",
        flow: ["Badge / biometrie", "Pontaj automat", "Raport live"],
        tags: ["Pontaj", "Acces", "Roluri", "Rapoarte"],
      },
      {
        icon: Wrench, title: "Intervenții pe teren",
        desc: "Tehnicienii primesc lucrări pe telefon, cu hartă, checklist și semnătură client. Totul se sincronizează și merge chiar și fără semnal.",
        flow: ["Lucrare pe telefon", "Checklist + foto", "Semnătură + GPS"],
        tags: ["Field service", "Offline", "GPS", "Semnătură"],
      },
      {
        icon: BarChart3, title: "ERP & raportare custom",
        desc: "Legăm datele din toate sistemele tale și le transformăm în rapoarte clare, automate, trimise pe email sau telefon exact când ai nevoie.",
        flow: ["Integrare API", "Reguli business", "Raport automat"],
        tags: ["ERP", "API", "Automatizare", "KPI"],
      },
      {
        icon: Network, title: "Multi-sediu, VPN & failover",
        desc: "Legăm toate sediile într-o singură rețea securizată: VPN site-to-site, load balancing pe mai multe conexiuni și failover automat. Dacă pică un internet sau un sediu, traficul trece instant pe rezervă — fără întreruperi, fără pierderi.",
        flow: ["VPN site-to-site", "Load balance / multi-WAN", "Failover automat"],
        tags: ["Multi-site", "VPN", "Load balance", "Failover", "Zero downtime"],
      },
    ],
    cta: "Spune-ne visul tău",
    ctaSub: "Îl transformăm în realitate.",
    sov: {
      label: "Datele tale rămân la tine",
      title: "Fără cloud rătăcit. Fără mâna vreunui hacker.",
      body: "Nu vindem virtualizare sau hosting la pachet. Construim infrastructura exact pe resursele tale — pe serverul tău, în locația ta. Datele stau fizic la tine, sub control total: nu se pierd prin cloud-uri străine și nu ajung pe mâna nimănui.",
      points: [
        { icon: Server, t: "Pe serverul tău", d: "Construit custom, pe fierul și nevoile tale reale" },
        { icon: Lock, t: "Control fizic total", d: "Datele rămân în locația ta, la tine" },
        { icon: ShieldCheck, t: "Departe de hackeri", d: "Suprafață de atac minimă, nu în cloud public" },
        { icon: HardDrive, t: "Zero vendor lock-in", d: "Fără abonamente și dependențe externe" },
      ],
    },
  },
  en: {
    label: "Custom software",
    h1: "From ZERO.",
    h2: "100% around your workflow.",
    sub: "We don't change the way you work — we mold to it. You tell us A, B, C… Z, and we turn your request into a real app: production, machinery, warehouse, maintenance — any workflow. All controlled from your phone: notifications, SMS, approvals. Wherever you are.",
    colClient: "The client asks",
    colTransform: "We transform",
    colPhone: "Reality, in your pocket",
    requests: [
      "I want to see stock in real time",
      "SMS alert when a machine stops",
      "approvals straight from my phone",
      "automatic report at end of shift",
      "QR scan on every order",
      "digital signature in the field",
    ],
    steps: ["Workflow analysis", "Design & architecture", "Custom development", "Live + support"],
    appName: "DMO · Live Ops",
    live: "LIVE",
    notifs: [
      { type: "alert", title: "Machine #3 — STOPPED", sub: "Line B · 12s ago" },
      { type: "sms", title: "SMS sent", sub: "Order #4821 approved" },
      { type: "ok", title: "Stock updated", sub: "+120 pcs · Warehouse A" },
      { type: "task", title: "Maintenance scheduled", sub: "Tomorrow 08:00 · Press 2" },
      { type: "sign", title: "Signature received", sub: "Delivery note · Client X" },
      { type: "call", title: "Auto dispatch call", sub: "High priority" },
    ],
    mobileTitle: "Everything from your phone",
    mobileSub: "Any action, anywhere — without being tied to a desk.",
    chips: [
      { icon: Bell, t: "Push notifications" },
      { icon: MessageSquare, t: "SMS & alerts" },
      { icon: CheckCircle2, t: "1-tap approvals" },
      { icon: QrCode, t: "QR / code scanning" },
      { icon: PenTool, t: "Digital signature" },
      { icon: BarChart3, t: "Live reports" },
      { icon: Wifi, t: "Offline mode" },
      { icon: MapPin, t: "GPS & location" },
    ],
    examplesTitle: "Examples of workflows we could transform",
    examplesSub: "A few real scenarios. Yours is different? Perfect — we build it exactly how you work.",
    examples: [
      {
        icon: Factory, title: "Production & machinery",
        desc: "Monitor machines in real time: downtime, OEE, consumption. When a machine stops you get an instant SMS and a maintenance ticket opens automatically.",
        flow: ["Sensors / PLC", "Live dashboard", "SMS + ticket"],
        tags: ["OEE", "Downtime", "Maintenance", "Alerts"],
      },
      {
        icon: Warehouse, title: "Warehouse & logistics",
        desc: "Live stock on your phone, QR scanning on receiving and shipping, delivery notes generated automatically. Zero paper, zero counting errors.",
        flow: ["QR scan", "Real-time stock", "Auto note"],
        tags: ["Scanning", "Stock", "Orders", "Traceability"],
      },
      {
        icon: Fingerprint, title: "Access control & attendance",
        desc: "Automatic time tracking, role-based access and attendance reports. Managers see who's on shift live and get alerted on events.",
        flow: ["Badge / biometrics", "Auto attendance", "Live report"],
        tags: ["Attendance", "Access", "Roles", "Reports"],
      },
      {
        icon: Wrench, title: "Field service",
        desc: "Technicians receive jobs on their phone, with map, checklist and client signature. Everything syncs and works even with no signal.",
        flow: ["Job on phone", "Checklist + photo", "Signature + GPS"],
        tags: ["Field service", "Offline", "GPS", "Signature"],
      },
      {
        icon: BarChart3, title: "Custom ERP & reporting",
        desc: "We connect data from all your systems and turn it into clear, automatic reports, delivered by email or phone exactly when you need them.",
        flow: ["API integration", "Business rules", "Auto report"],
        tags: ["ERP", "API", "Automation", "KPI"],
      },
      {
        icon: Network, title: "Multi-site, VPN & failover",
        desc: "We link all your sites into one secure network: site-to-site VPN, load balancing across multiple links and automatic failover. If an ISP or a site goes down, traffic instantly switches to backup — no interruptions, no data loss.",
        flow: ["Site-to-site VPN", "Load balance / multi-WAN", "Auto failover"],
        tags: ["Multi-site", "VPN", "Load balance", "Failover", "Zero downtime"],
      },
    ],
    cta: "Tell us your dream",
    ctaSub: "We turn it into reality.",
    sov: {
      label: "Your data stays yours",
      title: "No lost cloud. No hacker's hands.",
      body: "We don't sell packaged virtualization or hosting. We build the infrastructure exactly on your resources — on your own server, in your own location. Data stays physically with you, under full control: it doesn't get lost in foreign clouds and never ends up in anyone's hands.",
      points: [
        { icon: Server, t: "On your server", d: "Built custom, on your real hardware and needs" },
        { icon: Lock, t: "Full physical control", d: "Data stays in your location, with you" },
        { icon: ShieldCheck, t: "Away from hackers", d: "Minimal attack surface, not in a public cloud" },
        { icon: HardDrive, t: "Zero vendor lock-in", d: "No subscriptions, no external dependencies" },
      ],
    },
  },
};

const NOTIF_STYLE = {
  alert: { icon: Bell, ring: "text-red-400", chip: "bg-red-500/15 border-red-500/30" },
  sms: { icon: MessageSquare, ring: "text-cyan-neon", chip: "bg-cyan-neon/15 border-cyan-neon/30" },
  ok: { icon: CheckCircle2, ring: "text-emerald-400", chip: "bg-emerald-500/15 border-emerald-500/30" },
  task: { icon: ClipboardCheck, ring: "text-violet-300", chip: "bg-violet-500/15 border-violet-500/30" },
  sign: { icon: PenTool, ring: "text-cyan-neon", chip: "bg-cyan-neon/15 border-cyan-neon/30" },
  call: { icon: PhoneCall, ring: "text-amber-300", chip: "bg-amber-500/15 border-amber-500/30" },
};

// Animated "data-flow" node network — a lightweight looping background texture
const NetworkBg = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let w = 0, h = 0, dpr = 1, raf = 0, nodes = [];

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      if (!w || !h) return;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.max(24, Math.min(64, Math.floor((w * h) / 26000)));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.28,
        vy: (Math.random() - 0.5) * 0.28,
        r: Math.random() * 1.4 + 0.6,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const n of nodes) {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist < 140) {
            const op = (1 - dist / 140) * 0.16;
            ctx.strokeStyle = `rgba(0,229,255,${op})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      for (const n of nodes) {
        ctx.fillStyle = "rgba(0,229,255,0.55)";
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!reduce) raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="cs-network-bg"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full opacity-70"
    />
  );
};

// Typewriter that cycles through the client requests
const TypeCycle = ({ lines }) => {
  const [i, setI] = useState(0);
  const [len, setLen] = useState(0);
  const [del, setDel] = useState(false);

  useEffect(() => { setI(0); setLen(0); setDel(false); }, [lines]);

  useEffect(() => {
    const cur = lines[i] || "";
    let to;
    if (!del && len < cur.length) to = setTimeout(() => setLen(len + 1), 42);
    else if (!del && len === cur.length) to = setTimeout(() => setDel(true), 1500);
    else if (del && len > 0) to = setTimeout(() => setLen(len - 1), 20);
    else { setDel(false); setI((i + 1) % lines.length); }
    return () => clearTimeout(to);
  }, [len, del, i, lines]);

  return (
    <span className="text-white">
      {(lines[i] || "").slice(0, len)}
      <span className="ml-0.5 inline-block h-4 w-1.5 translate-y-0.5 animate-pulse bg-cyan-neon" />
    </span>
  );
};

// Phone with a live-updating notification feed
const PhoneMockup = ({ data }) => {
  const idRef = useRef(0);
  const mk = (n) => ({ ...n, uid: idRef.current++ });
  const [feed, setFeed] = useState(() => data.notifs.slice(0, 3).map(mk));

  useEffect(() => {
    setFeed(data.notifs.slice(0, 3).map(mk));
    let n = 3;
    const id = setInterval(() => {
      setFeed((prev) => [mk(data.notifs[n % data.notifs.length]), ...prev].slice(0, 4));
      n += 1;
    }, 2300);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div className="relative mx-auto w-[280px]">
      {/* glow */}
      <div className="pointer-events-none absolute -inset-8 -z-10 rounded-[3rem] bg-cyan-neon/10 blur-[70px]" />
      <motion.div
        initial={{ opacity: 0, y: 40, rotate: -2 }}
        whileInView={{ opacity: 1, y: 0, rotate: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="relative rounded-[2.6rem] border border-white/15 bg-gradient-to-b from-ink-700 to-ink-900 p-2.5 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
      >
        {/* notch */}
        <div className="absolute left-1/2 top-2.5 z-20 h-5 w-24 -translate-x-1/2 rounded-b-2xl bg-black" />
        <div className="relative h-[520px] overflow-hidden rounded-[2rem] bg-ink-900">
          <div className="absolute inset-0 grid-lines opacity-30" />
          {/* status bar */}
          <div className="relative flex items-center justify-between px-5 pt-3 font-mono text-[10px] text-white/60">
            <span>09:41</span>
            <div className="flex items-center gap-1.5">
              <Signal size={11} /><Wifi size={11} />
              <span className="ml-0.5 h-2.5 w-4 rounded-[3px] border border-white/40 bg-white/70" />
            </div>
          </div>
          {/* app header */}
          <div className="relative mt-4 flex items-center justify-between px-5">
            <div className="flex items-center gap-2">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-cyan-neon text-black">
                <Cpu size={16} />
              </span>
              <span className="font-display text-sm font-bold text-white">{data.appName}</span>
            </div>
            <span className="flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-emerald-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
              {data.live}
            </span>
          </div>
          {/* mini KPI */}
          <div className="relative mx-5 mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">Utilaje</div>
              <div className="mt-1 font-display text-lg font-extrabold text-white">12<span className="text-white/30">/14</span></div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">OEE</div>
              <div className="mt-1 font-display text-lg font-extrabold text-cyan-neon">87%</div>
            </div>
          </div>
          {/* notification feed */}
          <div className="relative mt-4 px-4">
            <AnimatePresence initial={false} mode="popLayout">
              {feed.map((n) => {
                const s = NOTIF_STYLE[n.type] || NOTIF_STYLE.ok;
                const Icon = s.icon;
                return (
                  <motion.div
                    key={n.uid}
                    layout
                    initial={{ opacity: 0, y: -18, scale: 0.94 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.25 } }}
                    transition={{ duration: 0.45, ease: EASE }}
                    className="mb-2 flex items-center gap-3 rounded-xl border border-white/10 bg-ink-800/90 p-3 backdrop-blur-sm"
                  >
                    <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg border ${s.chip} ${s.ring}`}>
                      <Icon size={15} />
                    </span>
                    <div className="min-w-0">
                      <div className="truncate font-display text-[12px] font-bold text-white">{n.title}</div>
                      <div className="truncate font-mono text-[9px] text-white/45">{n.sub}</div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export const CustomSoftware = () => {
  const { lang } = useI18n();
  const d = C[lang === "en" ? "en" : "ro"];

  const goContact = () => {
    const lenis = lenisRef.current;
    if (lenis) lenis.scrollTo("#contact", { offset: -60, duration: 1.2 });
    else document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="solutii"
      data-testid="custom-software-section"
      className="relative overflow-hidden border-t border-white/10 bg-ink-900"
    >
      <div className="absolute inset-0 grid-lines opacity-20" />
      <NetworkBg />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-ink-900 via-ink-900/40 to-ink-900" />
      <div className="pointer-events-none absolute -left-32 top-1/3 h-[34rem] w-[34rem] rounded-full bg-cyan-neon/10 blur-[150px]" />
      <div className="pointer-events-none absolute -right-40 top-10 h-[28rem] w-[28rem] rounded-full bg-[#7C5CFC]/10 blur-[150px]" />

      <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        {/* Header */}
        <div className="max-w-4xl">
          <Reveal>
            <SectionLabel index="[ CUSTOM ]">{d.label}</SectionLabel>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-7xl">
              {d.h1}{" "}
              <span className="text-aurora text-aurora-glow">{d.h2}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl font-body text-base leading-relaxed text-white/60 sm:text-lg">
              {d.sub}
            </p>
          </Reveal>
        </div>

        {/* Flow: request -> transform -> phone */}
        <div className="mt-16 grid grid-cols-1 items-stretch gap-6 lg:grid-cols-12">
          {/* Terminal — client asks */}
          <Reveal delay={0.05} className="lg:col-span-5">
            <div className="card-edge relative flex h-full flex-col overflow-hidden rounded-2xl" data-testid="cs-terminal">
              <div className="flex items-center gap-2 border-b border-white/10 bg-ink-800 px-4 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
                <span className="ml-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                  <Terminal size={12} /> {d.colClient}
                </span>
              </div>
              <div className="flex-1 space-y-3 p-6 font-mono text-sm">
                <div className="text-white/40">$ client --request</div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-cyan-neon">&gt;</span>
                  <TypeCycle lines={d.requests} />
                </div>
                <div className="pt-2 text-white/30">$ dmo transform --workflow</div>
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 size={14} /> ready to build…
                </div>
              </div>
            </div>
          </Reveal>

          {/* Transform pipeline */}
          <Reveal delay={0.1} className="lg:col-span-3">
            <div className="card-edge relative flex h-full flex-col overflow-hidden rounded-2xl p-6" data-testid="cs-pipeline">
              <div className="mb-5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-neon">
                <Sparkles size={13} /> {d.colTransform}
              </div>
              <div className="relative flex-1 pl-6">
                {/* spine */}
                <div className="absolute left-[7px] top-1 h-[calc(100%-1rem)] w-px bg-gradient-to-b from-cyan-neon/60 via-cyan-neon/20 to-transparent" />
                {[0, 1, 2].map((k) => (
                  <motion.span
                    key={k}
                    className="absolute left-[3px] top-1 h-2 w-2 rounded-full bg-cyan-neon shadow-[0_0_10px_rgba(0,229,255,0.8)]"
                    animate={{ y: [0, 200], opacity: [0, 1, 1, 0] }}
                    transition={{ duration: 2.2, repeat: Infinity, delay: k * 0.7, ease: "linear" }}
                  />
                ))}
                <div className="space-y-6">
                  {d.steps.map((s, i) => (
                    <div key={s} className="relative">
                      <span className="absolute -left-6 top-0.5 grid h-3.5 w-3.5 place-items-center rounded-full border border-cyan-neon/50 bg-ink-900">
                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-neon" />
                      </span>
                      <div className="font-mono text-[10px] text-white/30">0{i + 1}</div>
                      <div className="font-display text-sm font-semibold text-white">{s}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          {/* Phone */}
          <div className="lg:col-span-4" data-testid="cs-phone">
            <div className="mb-4 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 lg:justify-start">
              <Smartphone size={13} className="text-cyan-neon" /> {d.colPhone}
            </div>
            <PhoneMockup data={d} />
          </div>
        </div>

        {/* Data sovereignty highlight */}
        <Reveal delay={0.05}>
          <div
            data-testid="cs-sovereignty"
            className="relative mt-16 overflow-hidden rounded-2xl border border-cyan-neon/25 bg-gradient-to-br from-cyan-neon/10 via-ink-800 to-ink-900 p-8 sm:p-10"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-cyan-neon/15 blur-3xl" />
            <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <div className="mb-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-neon">
                  <ShieldCheck size={14} /> {d.sov.label}
                </div>
                <h3 className="font-display text-2xl font-extrabold leading-tight tracking-tight text-white sm:text-3xl">
                  {d.sov.title}
                </h3>
                <p className="mt-4 font-body text-sm leading-relaxed text-white/60 sm:text-base">
                  {d.sov.body}
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:col-span-7">
                {d.sov.points.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <motion.div
                      key={p.t}
                      data-testid={`cs-sov-point-${i}`}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.45, ease: EASE, delay: i * 0.06 }}
                      className="flex items-start gap-3 rounded-xl border border-white/10 bg-ink-900/60 p-4"
                    >
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-cyan-neon/30 bg-cyan-neon/10 text-cyan-neon">
                        <Icon size={18} />
                      </span>
                      <div>
                        <div className="font-display text-sm font-bold text-white">{p.t}</div>
                        <div className="mt-0.5 font-body text-xs leading-relaxed text-white/50">{p.d}</div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Everything from your phone — chips */}
        <div className="mt-20">
          <Reveal>
            <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h3 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                  {d.mobileTitle}
                </h3>
                <p className="mt-2 font-body text-sm text-white/55">{d.mobileSub}</p>
              </div>
            </div>
          </Reveal>
          <div className="mt-6 flex flex-wrap gap-2.5" data-testid="cs-mobile-chips">
            {d.chips.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.span
                  key={c.t}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, ease: EASE, delay: (i % 8) * 0.05 }}
                  className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-ink-800 px-4 py-2.5 font-mono text-xs text-white/70 transition-colors duration-300 hover:border-cyan-neon/50 hover:text-white"
                >
                  <Icon size={15} className="text-cyan-neon" /> {c.t}
                </motion.span>
              );
            })}
          </div>
        </div>

        {/* Example workflows */}
        <div className="mt-20">
          <Reveal>
            <h3 className="max-w-3xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
              {d.examplesTitle}
            </h3>
            <p className="mt-2 max-w-2xl font-body text-sm text-white/55">{d.examplesSub}</p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {d.examples.map((ex, i) => {
              const Icon = ex.icon;
              return (
                <motion.article
                  key={ex.title}
                  data-testid={`cs-example-${i}`}
                  initial={{ opacity: 0, y: 26 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, ease: EASE, delay: (i % 3) * 0.08 }}
                  {...spotlightTilt(6)}
                  style={{ willChange: "transform" }}
                  className="spotlight card-edge group relative flex h-full flex-col overflow-hidden rounded-2xl p-7"
                >
                  <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-cyan-neon/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
                  <span className="grid h-14 w-14 place-items-center border border-white/10 bg-ink-900 text-white/70 transition-colors duration-300 group-hover:border-cyan-neon/40 group-hover:text-cyan-neon">
                    <Icon size={26} strokeWidth={1.6} />
                  </span>
                  <h4 className="mt-6 font-display text-xl font-bold tracking-tight text-white">{ex.title}</h4>
                  <p className="mt-3 font-body text-sm leading-relaxed text-white/55">{ex.desc}</p>

                  {/* flow */}
                  <div className="mt-5 flex flex-wrap items-center gap-1.5">
                    {ex.flow.map((f, k) => (
                      <span key={f} className="flex items-center gap-1.5">
                        <span className="rounded-md border border-cyan-neon/20 bg-cyan-neon/5 px-2 py-1 font-mono text-[10px] text-cyan-neon/90">{f}</span>
                        {k < ex.flow.length - 1 && <ArrowRight size={12} className="text-white/25" />}
                      </span>
                    ))}
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2 border-t border-white/10 pt-5">
                    {ex.tags.map((tg) => (
                      <span key={tg} className="border border-white/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-white/45">{tg}</span>
                    ))}
                  </div>
                </motion.article>
              );
            })}

            {/* CTA card */}
            <motion.button
              data-testid="cs-cta"
              onClick={goContact}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, ease: EASE, delay: 0.16 }}
              className="group relative flex h-full min-h-[240px] flex-col justify-between overflow-hidden rounded-2xl border border-cyan-neon/40 bg-gradient-to-br from-cyan-neon/15 via-ink-800 to-ink-900 p-7 text-left transition-colors duration-300 hover:border-cyan-neon"
            >
              <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-cyan-neon/20 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />
              <Sparkles size={26} className="text-cyan-neon" />
              <div>
                <div className="font-display text-2xl font-extrabold leading-tight text-white">{d.cta}</div>
                <div className="mt-1 font-body text-sm text-white/60">{d.ctaSub}</div>
                <span className="mt-5 inline-flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.15em] text-cyan-neon">
                  {d.cta} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
};
